import { type FC, type FormEvent, useEffect, useState } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { FormSaveBar } from '../../../../components/FormSaveBar/FormSaveBar'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  useDeleteSchedulingEventTypeMutation,
  useDuplicateSchedulingEventTypeMutation,
  useGetAccountSchedulingSettingsQuery,
  useGetEventTypeCoHostsQuery,
  useGetEventTypeHostsQuery,
  useGetSchedulingEventTypeByIdQuery,
  useGetSchedulingLocationsQuery,
  useReplaceEventTypeCoHostListMutation,
  useReplaceEventTypeHostsMutation,
  useUpsertSchedulingEventTypeMutation,
  usePatchSchedulingEventTypeMutation,
} from '../../../../store/scheduling/scheduling'
import {
  type AccountSchedulingSettings,
  type CoHostScope,
  EventTypeAccess,
  type EventTypeCoHost,
  type EventTypeCoHosts,
  type EventTypeHost,
  type Location,
  MeetingMode,
  type SchedulingEventType,
} from '../../../../models/Scheduling'
import { EventTypeImpactDialog } from '../../../Scheduling/shared/EventTypeImpactDialog'
import { isSameRedtailEntry, ownRedtailEntry, toEventConfigInput } from '../../../Scheduling/shared/eventConfig'
import { useRedtailEventTypeAccess } from '../../../Scheduling/shared/useRedtailEventConfig'
import { useCanEditAccountScheduling } from '../../../Scheduling/shared/useCanEditAccountScheduling'
import { accountBookingPagePath, type EventTypeEditorTab, eventTypeEditorPath, schedulingSetupPath } from '../../../Scheduling/shared/bookingNavigation'
import { isSymbolicLocationKind } from '../../../Scheduling/flows/form/scheduleHelpers'
import { isOnAccountPage } from '../../../Scheduling/shared/eventTypeHostState'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { DetailsTab } from './tabs/DetailsTab'
import { AvailabilityTab } from './tabs/AvailabilityTab'
import { type BookingPageToggle, BookingPagesTab } from './tabs/BookingPagesTab'
import { EventTypeSummaryCard } from './sidebar/EventTypeSummaryCard'
import { EditorActions } from './sidebar/EditorActions'
import { EditEventTypeModal } from './EditEventTypeModal'

const FORM_ID = 'event-type-editor-form'
const EDITOR_TAB_KEYS: readonly EventTypeEditorTab[] = ['availability', 'booking-pages', 'details']
const LEGACY_TAB_KEYS: Record<string, EventTypeEditorTab> = { hosts: 'booking-pages' }
const DEFAULT_TAB: EventTypeEditorTab = 'availability'
const DEFAULT_BACK_PATH = schedulingSetupPath

type EditorTabKey = EventTypeEditorTab

const originLabel = (origin?: string): string => (origin === accountBookingPagePath ? 'Event types on the account booking page' : 'Shared event types')

interface BookingPagesTabTitleProps {
  needsHosts: boolean
}

const BookingPagesTabTitle: FC<BookingPagesTabTitleProps> = ({ needsHosts }) => (
  <span className='d-inline-flex align-items-center zocks-gap-0.5'>
    {needsHosts ? <i className='bi bi-circle-fill zocks-text-warning-dark' aria-label='Needs hosts' /> : null}
    Booking pages
  </span>
)

const coHostInputs = (coHosts: EventTypeCoHost[]) => coHosts.map((coHost) => ({ userId: coHost.userId, required: coHost.required }))

const SERVER_OWNED_FIELDS: ReadonlyArray<keyof SchedulingEventType> = ['hostMode', 'hostSummary', 'myCoHostScopes', 'updatedAt', 'usedByCount']
const eventTypeSnapshot = (eventType: SchedulingEventType) =>
  JSON.stringify(Object.fromEntries(Object.entries(eventType).filter(([key]) => !SERVER_OWNED_FIELDS.includes(key as keyof SchedulingEventType))))

const useEditorEntry = (): { origin?: string; mode?: string } => {
  const location = useLocation()
  return (location.state as { origin?: string; mode?: string } | null) ?? {}
}

interface EventTypeEditorProps {
  eventType: SchedulingEventType
  hosts?: EventTypeHost[]
  coHosts?: EventTypeCoHosts
  accountSettings?: AccountSchedulingSettings
  onRetryHosts: () => void
  onRetryCoHosts: () => void
  locations: Location[]
  activeTab: EditorTabKey
  disabled: boolean
  canEdit: boolean
  onTabChange: (tab: EditorTabKey) => void
  onEnableEditing: () => void
}

const EventTypeEditor: FC<EventTypeEditorProps> = ({
  eventType,
  hosts,
  coHosts,
  onRetryHosts,
  onRetryCoHosts,
  accountSettings,
  locations,
  activeTab,
  disabled,
  canEdit,
  onTabChange,
  onEnableEditing,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const { origin } = useEditorEntry()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const [upsertEventType, { isLoading: isSaving }] = useUpsertSchedulingEventTypeMutation()
  const [patchEventType] = usePatchSchedulingEventTypeMutation()
  const [replaceHosts] = useReplaceEventTypeHostsMutation()
  const [replaceCoHostList] = useReplaceEventTypeCoHostListMutation()
  const [duplicateEventType] = useDuplicateSchedulingEventTypeMutation()
  const [deleteEventType, { isLoading: isDeleting }] = useDeleteSchedulingEventTypeMutation()

  const [draft, setDraft] = useState<SchedulingEventType>(eventType)
  const redtailAccess = useRedtailEventTypeAccess(accountId, draft.access)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isEditDetailsOpen, setIsEditDetailsOpen] = useState(false)
  const [showSharedBanner, setShowSharedBanner] = useState(true)

  const isDirty = eventTypeSnapshot(draft) !== eventTypeSnapshot(eventType)
  const requiresLocation = draft.meetingMode === MeetingMode.IN_PERSON
  const isValid =
    draft.name.trim().length > 0 &&
    (!requiresLocation || (draft.location?.id != null && draft.location.id !== '') || isSymbolicLocationKind(draft.location?.kind))
  const needsHosts =
    isOnAccountPage(draft, accountSettings?.publicBookingPage.accountBookingEnabled === true) && hosts != null && !hosts.some((host) => host.eligible)

  const handleDraftChange = (patch: Partial<SchedulingEventType>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }

  const handleDiscard = () => {
    setDraft(eventType)
  }

  const showPartToast = (part: string, saved: boolean) => {
    dispatch(
      saved
        ? addToast({ variant: 'success', title: `${part.charAt(0).toUpperCase()}${part.slice(1)} saved`, messages: [`${eventType.name} was updated.`] })
        : addToast({ variant: 'error', title: `Could not save the ${part}`, messages: ['Please try again.'] }),
    )
  }

  const changedEventConfig = (next: SchedulingEventType) =>
    redtailAccess.enabled &&
    !isSameRedtailEntry(
      ownRedtailEntry(next.eventConfig, redtailAccess.redtailAccountId),
      ownRedtailEntry(eventType.eventConfig, redtailAccess.redtailAccountId),
    )
      ? { eventConfig: toEventConfigInput(next.eventConfig, redtailAccess.redtailAccountId) }
      : {}

  const finishSave = (result: { data: SchedulingEventType } | { error: unknown }, next: SchedulingEventType): SchedulingEventType | null => {
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not save the event type', messages: ['Please try again.'] }))
      return null
    }
    trackingClient.track({
      eventName: TrackEvent.SCHEDULING_EVENT_TYPE_UPDATED,
      eventDetails: { eventTypeId: next.id, access: next.access, active: next.active },
    })
    return result.data
  }

  const persistEventType = async (next: SchedulingEventType): Promise<SchedulingEventType | null> =>
    finishSave(await upsertEventType({ accountId, eventType: next, ...changedEventConfig(next) }), next)

  const persistFields = async (patch: Partial<SchedulingEventType>): Promise<SchedulingEventType | null> => {
    const next = { ...eventType, ...patch }
    const saved = finishSave(
      await patchEventType({ accountId, eventTypeId: eventType.id, fields: patch, ...changedEventConfig(next), suppressGlobalErrorToast: true }),
      next,
    )
    if (saved) {
      const savedFields = Object.fromEntries(Object.keys(patch).map((key) => [key, saved[key as keyof SchedulingEventType]]))
      setDraft((current) => ({ ...current, ...savedFields }))
    }
    return saved
  }

  const handleDetailsSave = async (patch: Partial<SchedulingEventType>) => {
    const saved = await persistFields(patch)
    if (!saved) {
      return
    }
    setIsEditDetailsOpen(false)
    dispatch(addToast({ variant: 'success', title: 'Event type saved', messages: [`${saved.name} was updated.`] }))
  }

  const handleToggleChange = async (toggle: BookingPageToggle): Promise<boolean> => (await persistFields(toggle)) != null

  const handleHostsChange = async (nextHosts: EventTypeHost[]): Promise<boolean> => {
    const result = await replaceHosts({
      accountId,
      eventTypeId: eventType.id,
      userIds: nextHosts.map((host) => host.userId),
      suppressGlobalErrorToast: true,
    })
    const saved = !('error' in result)
    showPartToast('hosts', saved)
    return saved
  }

  const handleCoHostsChange = async (scope: CoHostScope, coHosts: EventTypeCoHost[]): Promise<boolean> => {
    const result = await replaceCoHostList({
      accountId,
      eventTypeId: eventType.id,
      scope,
      coHosts: coHostInputs(coHosts),
      suppressGlobalErrorToast: true,
    })
    const saved = !('error' in result)
    showPartToast('co-hosts', saved)
    return saved
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    const saved = await persistEventType(draft)
    if (!saved) {
      return
    }
    setDraft(saved)
    dispatch(addToast({ variant: 'success', title: 'Event type saved', messages: [`${saved.name} was updated.`] }))
  }

  const handleDuplicate = async () => {
    const eventTypeId = eventType.id
    const result = await duplicateEventType({ accountId, eventTypeId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not duplicate the event type', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_EVENT_TYPE_DUPLICATED, eventDetails: { access: eventType.access, eventTypeId } })
    dispatch(addToast({ variant: 'success', title: 'Event type duplicated', messages: [`${result.data.name} was created.`] }))
    navigate(eventTypeEditorPath(result.data.id, DEFAULT_TAB), { state: origin ? { origin } : undefined })
  }

  const handleDelete = async () => {
    const result = await deleteEventType({ accountId, eventTypeId: eventType.id })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not delete the event type', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_EVENT_TYPE_DELETED, eventDetails: { access: eventType.access } })
    dispatch(addToast({ variant: 'success', title: 'Event type deleted', messages: [`${eventType.name} was removed.`] }))
    setIsDeleteOpen(false)
    navigate(origin ?? DEFAULT_BACK_PATH)
  }

  return (
    <div className='d-flex flex-column min-vh-100'>
      <Row>
        <Col lg={8}>
          {draft.access === EventTypeAccess.ACCOUNT && !disabled && showSharedBanner ? (
            <div className='d-flex align-items-center justify-content-between gap-2 paragraph-small p-3 mb-3 border rounded-2 zocks-border-color-gray-200 zocks-bg-color-gray-100'>
              <span className='d-flex align-items-center zocks-gap-0.5'>
                <i className='bi bi-info-circle zocks-text-gray-600' />
                You&apos;re editing a shared event type. Changes apply to all users.
              </span>
              <Button variant='grayGhost' className='p-1' aria-label='Dismiss notice' onClick={() => setShowSharedBanner(false)}>
                <i className='bi bi-x-lg' />
              </Button>
            </div>
          ) : null}
          <form id={FORM_ID} onSubmit={handleSave}>
            <Tabs activeKey={activeTab} onSelect={(key) => onTabChange((key as EditorTabKey) ?? DEFAULT_TAB)} className='mb-4'>
              <Tab eventKey='availability' title='Availability'>
                <AvailabilityTab draft={draft} accountSettings={accountSettings} locations={locations} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
              <Tab eventKey='booking-pages' title={<BookingPagesTabTitle needsHosts={needsHosts} />}>
                <BookingPagesTab
                  draft={draft}
                  accountSettings={accountSettings}
                  hosts={hosts}
                  coHosts={coHosts}
                  disabled={disabled}
                  onToggleChange={handleToggleChange}
                  onHostsChange={handleHostsChange}
                  onCoHostsChange={handleCoHostsChange}
                  onRetryHosts={onRetryHosts}
                  onRetryCoHosts={onRetryCoHosts}
                />
              </Tab>
              <Tab eventKey='details' title='Details'>
                <DetailsTab draft={draft} disabled={disabled} onDraftChange={handleDraftChange} />
              </Tab>
            </Tabs>
          </form>
        </Col>
        <Col lg={4} className='d-flex flex-column gap-2'>
          <EventTypeSummaryCard draft={draft} accountSettings={accountSettings} disabled={disabled} onEdit={() => setIsEditDetailsOpen(true)} />
          <EditorActions
            eventType={draft}
            disabled={disabled}
            canEdit={canEdit}
            onEnableEditing={onEnableEditing}
            onDuplicate={handleDuplicate}
            onDelete={() => setIsDeleteOpen(true)}
          />
        </Col>
      </Row>
      <FormSaveBar
        formId={FORM_ID}
        isVisible={isDirty && activeTab !== 'booking-pages'}
        isSubmitting={isSaving}
        isValid={isValid}
        isDirty={isDirty}
        errors={{}}
        onDiscard={handleDiscard}
      />
      {isEditDetailsOpen ? (
        <EditEventTypeModal
          eventType={draft}
          accountSettings={accountSettings}
          locations={locations}
          isSaving={isSaving}
          onSave={(patch) => void handleDetailsSave(patch)}
          onCancel={() => setIsEditDetailsOpen(false)}
        />
      ) : null}
      {isDeleteOpen ? (
        <EventTypeImpactDialog variant='delete' eventType={eventType} isLoading={isDeleting} onConfirm={handleDelete} onCancel={() => setIsDeleteOpen(false)} />
      ) : null}
    </div>
  )
}

export const EventTypeEditorView: FC = () => {
  const { eventTypeId = '', tabType = DEFAULT_TAB } = useParams()
  const navigate = useNavigate()
  const { origin, mode } = useEditorEntry()
  const backPath = origin ?? DEFAULT_BACK_PATH
  const [isEditing, setIsEditing] = useState(mode !== 'view')
  const dispatch = useAppDispatch()
  const isAccountAdmin = useCanEditAccountScheduling()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: eventType, isLoading, isError } = useGetSchedulingEventTypeByIdQuery({ accountId, eventTypeId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const isAccountEventType = eventType?.access === EventTypeAccess.ACCOUNT
  const { data: hosts, isLoading: isLoadingHosts, refetch: refetchHosts } = useGetEventTypeHostsQuery({ accountId, eventTypeId }, { skip: !isAccountEventType })
  const {
    data: coHosts,
    isLoading: isLoadingCoHosts,
    refetch: refetchCoHosts,
  } = useGetEventTypeCoHostsQuery({ accountId, eventTypeId }, { skip: !isAccountEventType })

  const activeTab: EditorTabKey = (EDITOR_TAB_KEYS as readonly string[]).includes(tabType)
    ? (tabType as EditorTabKey)
    : (LEGACY_TAB_KEYS[tabType] ?? DEFAULT_TAB)

  const handleTabChange = (tab: EditorTabKey) => {
    navigate(eventTypeEditorPath(eventTypeId, tab), {
      replace: true,
      state: { origin, mode: isEditing ? undefined : 'view' },
    })
  }

  const handleEnableEditing = () => {
    setIsEditing(true)
    navigate(eventTypeEditorPath(eventTypeId, activeTab), { replace: true, state: { origin } })
  }

  useEffect(() => {
    if (isError) {
      dispatch(addToast({ variant: 'error', title: 'Could not load the event type', messages: ['Please try again.'] }))
    }
  }, [isError, dispatch])

  if (isError) {
    return <Navigate to={backPath} replace />
  }

  const canEdit = eventType != null && (eventType.access === EventTypeAccess.ACCOUNT ? isAccountAdmin : true)
  const disabled = !canEdit || !isEditing

  return (
    <Container>
      <Row className='mb-4'>
        <Col className='d-flex align-items-center gap-2'>
          <Button variant='grayGhost' className='p-2' aria-label='Back to scheduling settings' onClick={() => navigate(backPath)}>
            <i className='bi bi-arrow-left' />
          </Button>
          <div className='d-flex flex-column'>
            <span className='lead-small zocks-text-gray-500 text-uppercase'>{originLabel(origin)}</span>
            <h4 className='mb-0 zocks-text-gray-900'>{eventType?.name ?? ''}</h4>
          </div>
        </Col>
      </Row>
      {isLoading || !eventType || !accountSettings || (isAccountEventType && (isLoadingHosts || isLoadingCoHosts)) ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : (
        <EventTypeEditor
          key={eventType.id}
          eventType={eventType}
          hosts={hosts?.hosts}
          coHosts={coHosts}
          onRetryHosts={refetchHosts}
          onRetryCoHosts={refetchCoHosts}
          accountSettings={accountSettings}
          locations={locations ?? []}
          activeTab={activeTab}
          disabled={disabled}
          canEdit={canEdit}
          onEnableEditing={handleEnableEditing}
          onTabChange={handleTabChange}
        />
      )}
    </Container>
  )
}
